import React, { useEffect, useRef, useState } from "react";
import { getDateTime } from "src/app/shared/helpers/date-helper/DateFormats";
import { ChannelType } from "src/app/contact/~store/models/ChannelType";
import { IChatsItemProps } from "./~types/ChatsItemProps";

import './ChatsItem.css';
import { Button, Input } from 'reactstrap';
import { GetChatsMode } from 'src/app/chats/~store/models/enums/GetChatsMode';
import { ChatStatus } from 'src/app/chats/~store/models/Chat';
import {CHAT_NAME_MAX_LENGTH} from "src/~store/constants"
import { useTranslation } from 'react-i18next';
import i18n from "src/app/shared/localization/i18n";
import { Avatar } from "@mui/material";
import OperatorIcon from "@mui/icons-material/SupportAgent";
import ContactIcon from "@mui/icons-material/AccountCircle";
import { GET_FILE_BY_ID_API_ROUTE } from "src/app/chats-panel/~api/apiRoutes";
import { getTime } from "../../../../shared/helpers/date-helper/DateHelper";
import { IMessage } from "src/app/chats/~store/models/Message";
import ChatTypeIcon from "src/app/shared/components/chat-type-icon/ChatTypeIcon";
import { getChatTypeClass, getChatTypeName } from "src/app/shared/helpers/chat-name-helper/ChatTypeHelper";
import { OperatorType } from "../../../../operator/~store/models/enums/OperatorType";
import { GetColorFromString } from "src/app/shared/helpers/contact-avatar-helper/ContactAvatarHelper";

const SECONDS_NO_COLOR = 60;
const SECONDS_TO_GET_YELLOW = 180;
const SECONDS_TO_GET_RED = 300;

const ChatsItem = (props: IChatsItemProps) => {
    const { t } = useTranslation();
    const [isChatNameEditing, setChatNameEditing] = useState(false);
    const [editedName, setEditedName] = useState(
        props.chat.contact?.chatName?.length > 0 ? 
        props.chat.contact?.chatName :
        ""
    );

  useEffect(() => {
    setEditedName(
      props.chat.contact?.chatName?.length > 0
        ? props.chat.contact?.chatName
        : ""
    );
  }, [props.chat.chatId]);

  const chatNameEditorRef = useRef<unknown>(undefined);

  const { chat } = props;
  const channelLogo = getChatsItemMessengerLogo(
    chat.channelType ?? ChannelType.Vk
  );
  
  const displayOperator = chat.lastEffectiveOperator || chat.operator;
  const contactAvatar = getContactAvatar(chat.contact?.avatarId ?? null);
  const operatorAvatar = getOperatorAvatar(displayOperator?.avatarId ?? null);
  const fallbackIconColor = contactAvatar ? "#26a69a" : GetColorFromString(chat.contact?.externalId ?? chat.messenger?.externalId);

    const _getNotAnsweredMessageClassName = (): string => {
        const hasUnanswered = chat.notAnsweredClientMessagesCount != 0;
        const isMyChat = chat.operatorId === props.operatorId;

        if (chat.isClosed)
            return "not-answered-messages-count-closed-chat";

        if (props.chatsMode === GetChatsMode.withMe || props.chatsMode === GetChatsMode.all) {
            if (hasUnanswered && isMyChat)
                return "not-answered-messages-count-chat-with-not-answered-messages";
            if (hasUnanswered && !isMyChat)
                return "not-answered-messages-count-withme-chat";
            if (!hasUnanswered && isMyChat)
                return "not-answered-messages-count-chat-without-not-answered-messages";
            return "not-answered-messages-count-withme-chat";
        }
        if (hasUnanswered)
            return "not-answered-messages-count-chat-with-not-answered-messages";
        return "not-answered-messages-count-chat-without-not-answered-messages";
    };

  const _setFontWeight = (): string => {
    if (chat.isNew && chat.isClosed === false)
    {
      return "chat-item-without-message-from-operator";
    }
    return "chat-item-with-message-from-operator";
  };

  const _cancelEditing = () => {
    setChatNameEditing(false);
    setEditedName(
      props.chat.contact.chatName?.length > 0
        ? props.chat.contact.chatName
        : ""
    );
  };

  const _saveEditedChatName = () => {
    setChatNameEditing(false);
    props.changeChatContactChatname(
      { ...props.chat.contact, chatName: editedName },
      props.chat.chatId
    );
  };

  const setFontWeight = _setFontWeight();
  const notAnsweredMessageClassName = _getNotAnsweredMessageClassName();

  const _calculateBackground = () => {
      if (
          !props.chat.firstNotAnsweredMessage ||
          props.chatsMode === GetChatsMode.archive ||
          props.chat.operator?.type === OperatorType.Bot
      )
          return "";

    const secondsClientWaiting =
      (new Date().getTime() -
        new Date(props.chat.firstNotAnsweredMessage.timeSent).getTime()) /
      1000;
      
        if(secondsClientWaiting < props.sLColorSecondsSettings.sLNoColorSeconds) return "0 2px 2px 0 #00000024, 0 3px 1px -2px rgb(0 0 0 / 12%), 0 1px 5px 0 rgb(0 0 0 / 20%)";
      if (secondsClientWaiting > props.sLColorSecondsSettings.sLRedSeconds) return "0px 0px 8px 1px #fa0a00";

        if(secondsClientWaiting <= props.sLColorSecondsSettings.sLYellowSeconds)
        {
            return "#ffff" + (+(255 - ((secondsClientWaiting - props.sLColorSecondsSettings.sLNoColorSeconds) * (255 / props.sLColorSecondsSettings.sLYellowSeconds))).toFixed()).toString(16) + "80";
        }
        
        return "#ff" + (+(255 - ((secondsClientWaiting - props.sLColorSecondsSettings.sLYellowSeconds) * (255 / (props.sLColorSecondsSettings.sLRedSeconds - props.sLColorSecondsSettings.sLYellowSeconds)))).toFixed()).toString(16) + "00";
    }

    const _onSelectChatHandler = () => {
      let selection = window?.getSelection()?.toString();
      if (!selection || selection.length <= 0) {
        props.onClick(props.chat.chatId)
      }
    };
    
    return (
        <tr style={_calculateBackground().startsWith("#") ? {boxShadow: _calculateBackground() + " 0px 0px 4px 4px"}: {boxShadow: _calculateBackground()}}
            onClick={() => _onSelectChatHandler()}
            onKeyDown={(event) => {
                if (event.key === "Enter") {
                  _onSelectChatHandler()
                }
            }}
            className={"chat-item__row" + (props.selected ? " chat-item__row-selected" : "")}
            data-testid="chats-list-row"
            data-chat-id={props.chat?.chatId}
            data-chat-closed={chat?.isClosed ? "true" : "false"}
            data-chat-status={chat?.chatStatsModel?.status ?? ""}
            tabIndex={0}
        >
            <td className="chat-item__contact-info-cell" colSpan={3}>
                <table>
                    <tbody>
                        <tr>
                            <td className={"chat-item__chat-id " + setFontWeight}>
                              #{props.chat.chatId}
                            </td>
                            <td className="chat-item__chat-type-icon-container"> 
                                <div className={`chat-item__chat-type ${getChatTypeClass(props.chat.chatType)}`}>{getChatTypeName(props.chat.chatType)}</div>
                            </td>
                            <td className="chat-item__chat-info" colSpan={3}>
                                <span className={"chat-item__not-answered-messages-count " + notAnsweredMessageClassName}>{chat.notAnsweredClientMessagesCount <= 99 ? chat.notAnsweredClientMessagesCount : "99+"}</span>
                                <span className={"chat-item__chat-created-time " + setFontWeight}>{getDateTime(chat.timeCreated)}</span>
                                {props.chat.onPause && props.chatsMode != GetChatsMode.archive && <span className="chat-item__chat-paused">{i18n.t('chats.pause')} {props.pausedTime}</span>}
                            </td>
                        </tr>
                        <tr>
                            <td className="chat-item__contact-info_avatar" rowSpan={2}>
                                <Avatar className="chat-item__contact-avatar" src={contactAvatar} >
                                    <ContactIcon style={{ color: fallbackIconColor }} />
                                </Avatar>
                            </td>
                            <td>
                                {!isChatNameEditing && <div 
                                    className={"chat-item__contact-info_name "+ setFontWeight} 
                                    onClick={() => {
                                        if(props.selected)
                                            setChatNameEditing(true);
                                    }}>
                                        {(chat.contact?.chatName?.length > 0) ? 
                                            (chat.contact?.chatName) :
                                            (chat.contact?.lastName?.length > 0 || chat.contact?.firstName?.length > 0) ?
                                            (chat.contact?.lastName + " " + chat.contact?.firstName) :
                                            (chat.contact?.nickName)
                                        }
                                </div>}
                                {isChatNameEditing && <div 
                                    className="chat-item__contact-info_name-editor"
                                    onBlur={() => _cancelEditing()}>
                                    <Input
                                        className="chat-item__contact-info_name-editor-input"
                                        autoFocus={true}
                                        maxLength={CHAT_NAME_MAX_LENGTH}
                                        placeholder={i18n.t('chats.enterChatName')}
                                        onKeyDown={(event) => {
                                            if(event.key === "Escape") {
                                                _cancelEditing();
                                            }
                                            if(event.key === "Enter") {
                                                _saveEditedChatName();
                                            }
                                        }}
                                        onChange={(event) => setEditedName(event.target.value)}
                                        value={editedName}/>
                                    <Button
                                        className="chat-item__contact-info_change-name-btn"
                                        onMouseDown={() => {
                                            _saveEditedChatName()
                                        }}>
                                        ok
                                    </Button>
                                </div>}
                            </td>
                            <td>
                                <div className={"chat-item__operator-info " + setFontWeight}>{!displayOperator?.isDeleted ? displayOperator?.name : "[Removed]"}</div>
                            </td>
                            <td className={`${props.isChatvisible ? 'chat-item__operator-info_avatar-chat-visible' : 'chat-item__operator-info_avatar'}`} rowSpan={2}>
                                <Avatar className="chat-item__operator-avatar" src={operatorAvatar}>
                                    <OperatorIcon style={{ color: '#26a69a'}} />
                                </Avatar>
                            </td>
                        </tr>
                        <tr>
                            <td className="chat-item__contact-message-column" colSpan={2}>
                                <div className="chat-item__contact-info_message line-clamp">
                                    {(getChatLastMessage(chat.lastMessage)) ?
                                        <span className={`${setFontWeight} ${chat.lastMessage?.isDeleted ? "deleted-text" : ""}`}>
                                          {
                                            props.chatsMode != GetChatsMode.archive && 
                                            <span className="chat-item-with-message-from-operator-message-time">
                                              {getTime(chat.lastMessage.timeSent)}
                                            </span>
                                          }
                                          <span className="chat-item__contact-info_sender">
                                            {(props.sender ? (props.sender + ":") : "")}
                                            </span> 
                                            <span className={chat.lastMessage?.isDeleted ? "content" : ""}>
                                                {getChatLastMessage(chat.lastMessage)}
                                            </span>
                                            {chat.lastMessage?.isDeleted && <span> &nbsp;({t('chat.deleted')})</span>} 
                                        </span> 
                                        : ""
                                        }
                                </div>
                            </td>
                            {props.isChatvisible && (
                                <td className="chat-item__messenger-logo-cell" rowSpan={2}>
                                    <img
                                        className="chat-item__messenger-img-open-chat"
                                        src={channelLogo}
                                    />
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            </td>
            {!props.isChatvisible && <td className="chat-item__messenger-info-cell">
                <table>
                    <tbody>
                        <tr>
                            <td>
                                <div className="chat-item__messenger">
                                    <img
                                        className="chat-item__messenger-img" 
                                        src={channelLogo}/>
                                    <div className="chat-item__channel-name">
                                        <div>{chat.channel?.name}</div>
                                    </div>
                                </div>
                                <div className="chat-item__is-closed-text">
                                    {chat.isClosed ? t('chats.closed') : t('chats.active')}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </td>}
            {props.chatsMode === GetChatsMode.archive && (
                <td className="chat-item__messenger-status-cell">{getChatStatusString(chat.chatStatsModel?.status)}</td>
                )}
            {!props.isChatvisible && <td>    
            </td>}
        </tr>
    )
}

const getChatStatusString = (status: ChatStatus | undefined) => {
  switch (status) {
    case ChatStatus.Expired:
      return i18n.t("chats.expired");
    case ChatStatus.Finished:
      return i18n.t("chats.finished");
    case ChatStatus.Missed:
      return i18n.t("chats.missed");
    default:
      return "";
  }
};

const getChatLastMessage = (message: IMessage | undefined) =>
{
  if(message?.text)
  {
    return message.text;
  }
  if(message?.imageId || message?.fileId || message?.location)
  {
    return i18n.t('chats.attachmentMessage');
  }
  return "";
}

const getChatsItemMessengerLogo = (channelType: ChannelType) => {
  switch (channelType) {
    case ChannelType.Telegram:
      return require("src/app/shared/images/Chat/telegram.svg").default;
    case ChannelType.Viber:
      return require("src/app/shared/images/Chat/viber.svg").default;
    case ChannelType.Vk:
      return require("src/app/shared/images/Chat/vk.svg").default;
    case ChannelType.Widget:
      return require("src/app/shared/images/Chat/widget.svg").default;
    case ChannelType.Facebook:
      return require("src/app/shared/images/Chat/facebook.svg").default;
    case ChannelType.WeChat:
      return require("src/app/shared/images/Chat/wechat.svg").default;
    case ChannelType.Instagram:
      return require("src/app/shared/images/Chat/instagram.svg").default;
    case ChannelType.UniformWidget:
      return require("src/app/shared/images/Chat/uniformwidget.svg").default;
    case ChannelType.EdnaWhatsApp:
      return require("src/app/shared/images/Chat/whatsapp-logo.svg").default;
    case ChannelType.WhatsApp:
          return require("src/app/shared/images/Chat/whatsapp-logo.svg").default;
    case ChannelType.VoiceChannel:
        return require("src/app/shared/images/Chat/voice-channel-logo.svg").default;
    case ChannelType.TelegramPrivate:
          return require("src/app/shared/images/Chat/telegram_private.svg").default;
    case ChannelType.SendPulse:
          return require("src/app/shared/images/Chat/sendPulse.svg").default;
    case ChannelType.Sms:
          return require("src/app/shared/images/Chat/sms.svg").default;
    case ChannelType.ViberAlfa:
          return require("src/app/shared/images/Chat/viber.svg").default;
  }
};

const userInStorage = sessionStorage.getItem("user");
const customerId = userInStorage === null ? "" : JSON.parse(userInStorage.toString()).customerId;

const getContactAvatar = (avatarId: string | null) => {
  return avatarId === null
    ? ''
    : GET_FILE_BY_ID_API_ROUTE + avatarId;
};

const getOperatorAvatar = (avatarId: string | null) => {
  return avatarId !== null
    ? GET_FILE_BY_ID_API_ROUTE + avatarId
    : '';
};

export default ChatsItem;
